import {
  ArrowPathIcon,
  ClockIcon,
  ListBulletIcon,
  UserMinusIcon,
  UserPlusIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import React from 'react';

import { CopyToClipboard } from '@/components/CopyToClipboard';
import { AegisManagementStatus } from '@/hooks/useAegisManagement';
import { TaskItemProps } from '@/sections/agents/types/index';
import {
  formatSafeTaskErrorMessage,
  getAegisCapabilityDescription,
  getAegisTaskGuidance,
  getAegisTaskStatusLabel,
} from '@/sections/agents/utils/index';
import { cn } from '@/utils/classname';
import { formatDate } from '@/utils/date.util';

/**
 * Component for displaying individual Aegis management tasks
 *
 * Provides:
 * - Expandable task details
 * - Status indicators with appropriate icons
 * - Safe error message display with XSS prevention
 * - Contextual guidance based on task status
 * - Proper accessibility support
 */
export const TaskItem: React.FC<TaskItemProps> = ({
  task,
  isExpanded,
  onToggleExpansion,
  minimal = false,
}) => {
  // Extract UUID from task key for consistent display
  const extractTaskUuid = (taskKey: string): string => {
    const parts = taskKey.split('#');
    return parts[parts.length - 1] || taskKey;
  };

  const taskId = extractTaskUuid(task.key);

  const getStatusIcon = () => {
    switch (task.status) {
      case AegisManagementStatus.COMPLETED:
        return <CheckCircleIcon className="text-chariot-stroke-info size-5" />;
      case AegisManagementStatus.FAILED:
        return <XCircleIcon className="text-chariot-status-critical size-5" />;
      case AegisManagementStatus.RUNNING:
        return (
          <ArrowPathIcon className="text-chariot-status-medium size-5 animate-spin" />
        );
      case AegisManagementStatus.PENDING:
        return <ArrowPathIcon className="text-chariot-text-secondary size-5" />;
      default:
        return <ArrowPathIcon className="text-chariot-text-secondary size-5" />;
    }
  };

  return (
    <div
      key={task.key}
      className="bg-chariot-background-secondary border-chariot-stroke overflow-hidden rounded-lg border shadow-sm transition-all"
    >
      {/* Task Header - Always Visible */}
      <div
        className="hover:bg-chariot-background-primary hover:bg-opacity-50 flex cursor-pointer items-center justify-between px-4 py-3 transition-all"
        onClick={() => onToggleExpansion(task.key)}
        data-testid={`task-${taskId}-header`}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-controls={`task-${taskId}-details`}
        aria-label={`${isExpanded ? 'Collapse' : 'Expand'} task details for ${getAegisCapabilityDescription(task.aegisManagementCapability)}`}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggleExpansion(task.key);
          }
        }}
      >
        <div className="flex min-w-0 flex-1 items-center gap-3">
          {/* Expand/Collapse Icon */}

          <div className="shrink-0">
            <CapabilityIcon capability={task.aegisManagementCapability} />
          </div>

          <div className="flex min-w-0 flex-1 items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-chariot-text-primary truncate text-sm">
                  {getAegisCapabilityDescription(
                    task.aegisManagementCapability
                  )}
                </span>
                {task.parameters?.username && (
                  <span className="border-chariot-stroke bg-chariot-background-primary text-chariot-text-secondary shrink-0 rounded border px-2 py-1 text-xs">
                    {task.parameters.username}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {task.created && (
            <span
              className="text-chariot-text-secondary text-xs"
              data-testid={`task-${taskId}-created`}
            >
              {formatDate(task.created)}
            </span>
          )}
          <div
            data-testid={`task-${taskId}-status`}
            aria-label={`Task status: ${getAegisTaskStatusLabel(task.status)}`}
          >
            {getStatusIcon()}
          </div>
        </div>
      </div>

      {/* Task Details - Collapsible */}
      {isExpanded && !minimal && (
        <div
          className="border-chariot-stroke bg-chariot-background-primary space-y-3 border-t p-4 text-xs"
          id={`task-${taskId}-details`}
          data-testid={`task-${taskId}-details`}
          role="region"
          aria-labelledby={`task-${taskId}-header`}
        >
          {/* Task Guidance Section */}
          {getAegisTaskGuidance(
            task.status,
            task.aegisManagementCapability
          ) && (
            <div
              className={cn(
                'bg-opacity-10 rounded-md border p-3',
                task.status === AegisManagementStatus.FAILED &&
                  'text-chariot-text-red',
                task.status === AegisManagementStatus.RUNNING &&
                  'border-chariot-stroke-medium bg-chariot-status-medium',
                task.status === AegisManagementStatus.PENDING &&
                  'border-chariot-stroke-info bg-chariot-status-info',
                task.status === AegisManagementStatus.COMPLETED &&
                  'border-chariot-stroke-info bg-chariot-status-info'
              )}
              data-testid={`task-${taskId}-guidance`}
            >
              <p className="text-chariot-text-primary text-xs">
                {getAegisTaskGuidance(
                  task.status,
                  task.aegisManagementCapability
                )}
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2 text-xs">
            {task.started && (
              <div className="flex items-center gap-1">
                <ClockIcon className="text-chariot-text-secondary mr-1 size-4" />
                <span className="text-chariot-text-secondary">Started:</span>
                <span>{formatDate(task.started)}</span>
              </div>
            )}
            {task.completed && (
              <div className="flex items-center gap-1">
                <ClockIcon className="text-chariot-text-secondary mr-1 size-4" />
                <span className="text-chariot-text-secondary">Completed:</span>
                <span>{formatDate(task.completed)}</span>
              </div>
            )}
          </div>
          <div className="min-w-0">
            <span className="text-chariot-text-secondary font-sm">
              Full Task ID:
            </span>
            <CopyToClipboard className="text-chariot-text-primary bg-chariot-background-primary border-chariot-stroke mt-1 rounded border p-2 font-mono text-xs">
              <span className="block truncate">{task.key}</span>
            </CopyToClipboard>
          </div>
          {task.parameters?.remove_home && (
            <div>
              <span className="text-chariot-text-secondary font-xs">
                Options:
              </span>
              <span className="text-chariot-text-primary bg-chariot-background-secondary border-chariot-stroke rounded border px-2 py-1 text-xs">
                Remove home directory
              </span>
            </div>
          )}
          {task.errorMessage && (
            <div>
              <span className="text-chariot-text-secondary font-xs">
                Error Details:
              </span>
              <div
                className="text-chariot-text-red bg-chariot-background-secondary border-chariot-stroke mt-1 rounded-md border p-3 font-mono text-xs"
                data-testid={`task-${taskId}-error`}
              >
                <span className="block pb-2">
                  {errorGuidance(task.aegisManagementCapability)}
                </span>
                <span>{formatSafeTaskErrorMessage(task.errorMessage)}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const errorGuidance = (capability: string) => {
  if (capability.includes('tunnel')) {
    return 'Tunnel operation failed. Check agent connectivity and Cloudflare configuration.';
  }
  if (capability.includes('user')) {
    return 'User operation failed. Verify system permissions and tunnel configuration.';
  }
  return 'Operation failed. Check the error details for more information.';
};

const CapabilityIcon = ({ capability }: { capability: string }) => {
  switch (capability) {
    case 'linux-system-adduser':
      return <UserPlusIcon className="text-chariot-text-secondary size-5" />;
    case 'linux-system-deluser':
      return <UserMinusIcon className="text-chariot-text-secondary size-5" />;
    case 'linux-cloudflared-install':
    case 'linux-cloudflared-remove':
      return <ListBulletIcon className="text-chariot-text-secondary size-5" />;
    default:
      return null;
  }
};
